import React, { useState, useCallback } from 'react'
import { Alert } from 'antd'
import { IconHourGlass, IconCheck, IconClose } from '@edulastic/icons'
import { white } from '@edulastic/colors'
import { segmentApi } from '@edulastic/api'
import {
  fetchIncompleteConversionsCount,
  INCOMPLETE_CONVERSIONS_POLL_INITIAL_MS,
  INCOMPLETE_CONVERSIONS_POLL_MAX_MS,
} from '../../utils/bulkConverterPearApi'
import {
  buildBulkConversionCsv,
  downloadCsvFile,
  getBulkConversionFileSummary,
} from '../../utils/bulkConversionCsv'
import { BULK_CONVERTER_SEGMENT } from '../../../constants/bulkConverterSegment'
import {
  BulkConvertStatusBarWrapper,
  BulkConvertStatusBarPanel,
  BulkConvertStatusBarLeft,
  BulkConvertStatusBarSummaryRow,
  BulkConvertStatusIconWrap,
  BulkConvertStatusMessage,
  BulkConvertStatusBarDisclaimer,
  BulkConvertStatusBarActions,
  BulkConvertDownloadButton,
  BulkConvertDismissButton,
  PdlBulkCsvDownloadIcon,
} from '../Container/styled'
import { StyledDisclaimerLink } from '../../../AIFeatures/common/styled'

/**
 * Bulk import banner on My Content: in-progress polling, then summary + CSV download + dismiss.
 */
const BulkConvertStatusBar = ({
  showInitially = false,
  bulkConversionId = null,
  conversion = null,
  csvReadyForDownload = false,
  tests = [],
  dismissed = false,
  onComplete,
  onDismiss,
}) => {
  const [incompleteCount, setIncompleteCount] = useState(null)
  const [csvBusy, setCsvBusy] = useState(false)

  const hasScopedId =
    bulkConversionId != null && String(bulkConversionId).trim() !== ''

  React.useEffect(() => {
    let cancelled = false
    let timeoutId = null
    let delay = INCOMPLETE_CONVERSIONS_POLL_INITIAL_MS

    setIncompleteCount(null)

    const conversionIds = hasScopedId
      ? [String(bulkConversionId).trim()]
      : undefined

    const poll = async () => {
      const { count } = await fetchIncompleteConversionsCount({ conversionIds })
      if (cancelled) return

      setIncompleteCount(count)
      if (count === 0) {
        onComplete?.()
        return
      }

      delay = Math.min(delay * 2, INCOMPLETE_CONVERSIONS_POLL_MAX_MS)
      timeoutId = setTimeout(poll, delay)
    }

    poll()

    return () => {
      cancelled = true
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [showInitially, bulkConversionId, hasScopedId, onComplete])

  const showComplete =
    !dismissed && Boolean(csvReadyForDownload && conversion && bulkConversionId)

  /** Same visibility rule as before: hide once Pear reports no incomplete work (until complete UI). */
  const showInProgress =
    !showComplete &&
    ((showInitially && incompleteCount === null) ||
      (hasScopedId && incompleteCount === null) ||
      (incompleteCount !== null && incompleteCount > 0))

  const handleCsvDownload = useCallback(() => {
    if (!showComplete || csvBusy) return
    setCsvBusy(true)
    try {
      const csv = buildBulkConversionCsv(conversion, tests)
      const safeId =
        conversion?.conversion_id ?? String(bulkConversionId || 'export')
      const stamp = conversion?.created_at
      downloadCsvFile(
        stamp
          ? `bulk-conversion-${stamp}-${safeId}.csv`
          : `bulk-conversion-${safeId}.csv`,
        csv
      )
      segmentApi.genericEventTrack(
        BULK_CONVERTER_SEGMENT.MY_CONTENT_BULK_CONVERSION_CSV_DOWNLOAD,
        {
          bulkConversionId,
          fileCount: conversion?.files?.length ?? conversion?.total_files ?? 0,
        }
      )
    } finally {
      setCsvBusy(false)
    }
  }, [showComplete, csvBusy, conversion, tests, bulkConversionId])

  if (!showInProgress && !showComplete) return null

  if (showComplete) {
    const { successCount, totalCount } = getBulkConversionFileSummary(
      conversion
    )
    const total = Math.max(0, Number(totalCount) || 0)
    const success = Math.max(0, Math.min(Number(successCount) || 0, total))
    const fileLabel = total === 1 ? 'File' : 'Files'
    const summary =
      total > 0
        ? `${success}/${total} ${fileLabel} uploaded successfully.`
        : 'Files uploaded successfully.'

    return (
      <BulkConvertStatusBarWrapper
        role="status"
        aria-live="polite"
        data-cy="bulkConvertStatusBarComplete"
      >
        <BulkConvertStatusBarPanel>
          <BulkConvertStatusBarLeft>
            <BulkConvertStatusBarSummaryRow>
              <BulkConvertStatusIconWrap aria-hidden>
                <IconCheck color={white} width={12} height={12} />
              </BulkConvertStatusIconWrap>
              <BulkConvertStatusMessage>{summary}</BulkConvertStatusMessage>
            </BulkConvertStatusBarSummaryRow>
            <BulkConvertStatusBarDisclaimer>
              Content Convertor uses AI to generate tests and meta data. It can
              make mistakes.{' '}
              <StyledDisclaimerLink
                href="https://www.peardeck.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </StyledDisclaimerLink>
            </BulkConvertStatusBarDisclaimer>
          </BulkConvertStatusBarLeft>
          <BulkConvertStatusBarActions>
            <BulkConvertDownloadButton
              type="button"
              data-cy="pdlBulkDownloadCsv"
              aria-label="Download files for this import"
              disabled={csvBusy}
              onClick={handleCsvDownload}
            >
              <PdlBulkCsvDownloadIcon aria-hidden />
              Download Summary
            </BulkConvertDownloadButton>
            <BulkConvertDismissButton
              type="button"
              aria-label="Dismiss upload summary"
              data-cy="bulkConvertStatusBarDismiss"
              onClick={onDismiss}
            >
              <IconClose width={12} height={12} />
            </BulkConvertDismissButton>
          </BulkConvertStatusBarActions>
        </BulkConvertStatusBarPanel>
      </BulkConvertStatusBarWrapper>
    )
  }

  return (
    <BulkConvertStatusBarWrapper role="status" aria-live="polite">
      <Alert
        type="info"
        message={
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 16,
            }}
          >
            <IconHourGlass width={24} height={24} />
            Creating test questions from your documents. This may take a few
            minutes.
          </div>
        }
      />
    </BulkConvertStatusBarWrapper>
  )
}

export default BulkConvertStatusBar
